<template>
    <SlideToRip
        :enabled="packRip.isEnabled.value && isDesktopLayout"
        :progress="packRip.ripProgress.value"
        @dragstart="packRip.setSliderDragging(true)"
        @drag="packRip.setProgress"
        @dragend="packRip.setSliderDragging(false)"
    />

    <SwipeTutorial ref="refSwipeTutorial" dir="y" :active="showSwipeTutorial && isMobileLayout" />

    <slot />
</template>

<script>
    import { useInteractionMotion } from '#imports';

    import { FlyInWaxTransition, FlyUpSpinTransition, RipWaxTransition } from './transitions';
    import SwipeTutorial from '~/components/common/SwipeTutorial.vue';
    import AsyncLabel from '~/components/gl/async-experience/AsyncLabel.vue';
    import ShineLayer from '~/components/gl/instant-rips/objects/GoldenTicket/GoldenShine';
    import { createLensflare } from '~/components/gl/Pack/elements/createLensflare';
    import { useRipWipe } from '~/components/gl/Pack/elements/useRipWipe';
    import { useManualPackRip } from '~/components/gl/Pack/transitions/useManualPackRip';
    import { createLogger } from '~/utils/logger';

    const packWaxLogger = createLogger('pack-wax');

    export const TRANSITIONS_REVEAL = {
        flyIn: FlyInWaxTransition,
        flyUpSpin: FlyUpSpinTransition,
    };

    export const TRANSITIONS_RIP = {
        rip: RipWaxTransition,
    };

    export const PackWaxProviderKey = 'PackWax';

    export const usePackWax = () => {
        const pack = inject(PackWaxProviderKey, null);

        if (!pack) {
            packWaxLogger.warn('PackWax provider not found', {
                event: 'provider.missing',
                provider: 'PackWax',
            });
            return {};
        }

        return pack;
    };
</script>

<script setup>
    import { useEventBus } from '@vueuse/core';
    import { Color, Group, Object3D, Texture, Vector3 } from 'three';
    import {
        computed,
        inject,
        onBeforeUnmount,
        onMounted,
        provide,
        reactive,
        ref,
        shallowRef,
        watch,
        watchEffect,
    } from 'vue';

    import { LoaderEvent } from '@resn/gozer-loading';
    import { lerp, map, randomInt, toRadians } from '@resn/gozer-math';
    import { disposeMaterial } from '@resn/gozer-three/helpers';
    import {
        basePaneOptions,
        usePane,
        useRafBool,
        useThreeObject,
        useViewportResize,
    } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import PackWaxMaterial from '../materials/PackWaxMaterial';
    import { getAllMeshes } from '../utils';
    import SlideToRip from './SlideToRip.vue';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { useEnvMapLoader } from '~/composables/useEnvMapLoader';
    import { useLayout } from '~/composables/useLayout';
    import { useLoaderComplete } from '~/composables/useLoaderComplete';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useAudio } from '~/providers/AudioProvider';
    import { useGlobalAssets } from '~/providers/GlobalAssets';
    import { useMatcap } from '~/providers/MatcapProvider';

    const PALETTE_DISSOLVE = ['#c5b3d2', '#b676cf', '#0a44df'];

    const props = defineProps({
        image: { default: '' },
        color: { default: '#ffffff' },
        category: { default: null, required: false },
        tier: { default: null, required: false },
        variant: { type: String, default: null },
        type: { type: String, default: 'wax' },
        username: { type: String, default: null },
        cards: { type: Array, default: () => [] },
        productName: { type: String, default: null },
        interactive: { type: Boolean, default: false },

        hasAmbientMotion: { type: Boolean, default: true },
        hasCompressedTextures: { type: Boolean, default: true },
        hasAutoLoad: { type: Boolean, default: true },

        swipeToOpen: { type: Boolean, default: false },
    });

    provide(PackWaxProviderKey, {});

    const refSwipeTutorial = ref(null);

    const active = ref(false);
    const image = computed(() => props.image);
    const packColor = computed(() => props.color);
    const testColor = ref('#ffffff');

    const { renderer } = inject('renderer');
    const { loadEnvMap } = useEnvMapLoader();
    const { isDesktopLayout, isMobileLayout } = useLayout();
    const interaction = useInteractionMotion();
    const showSwipeTutorial = ref(false);

    const propsReactive = reactive({
        speed: 0.8,
        motionAmb: 0,
        bumpScale: 1.5,
        envMapRotation: new Vector3(0, Math.PI * 2, 0),
        envMapIntensity: 1,
        mcScale: 2,
    });

    const propsTransition = reactive({
        progress: 0,
        animating: false,

        revealId: null,
        ripId: null,
    });

    const propsAnimation = {
        position: new Vector3(),
        positionTarget: new Vector3(),

        rotation: new Vector3(),
        rotationTarget: new Vector3(),

        reduceMotion: true,
    };

    const tempTexture = new Texture();

    const shaderDefault = new PackWaxMaterial();
    const shaders = [shaderDefault];

    const vPosStart = new Vector3();
    const vPosAmbient = new Vector3();
    const vPosInteraction = new Vector3();

    const vRotStart = new Vector3();
    const vRotSpin = new Vector3();
    const vRotAmbient = new Vector3();
    const _tempRotWax = new Vector3();

    const packObject = shallowRef(null);
    const packMeshes = shallowRef([]);

    const bgGlowColor = ref('#faf4ff'); // #fdf4ff | #faf4ff

    const grpCustomMeshes = new Group();
    grpCustomMeshes.name = 'CustomMeshes';

    const { object } = useThreeObject(null, { name: 'PackWax' });

    const innerObject = new Object3D();
    innerObject.name = 'Inner Object';
    object.add(innerObject);

    const lensflare = createLensflare({
        pos: { x: 0.11, y: 0.5, z: -0.02 },
        dir: 'y',
        distance: 1.07,
        color: '#ffffff',
        bgColor: '#7b7b7b',
        baseScale: 0.3,
    });
    innerObject.add(lensflare.object);

    const bgGlow = new ShineLayer(object);
    bgGlow.material.depthTest = true;
    bgGlow.material.uniforms.lightColor.value.set(bgGlowColor.value);
    bgGlow.renderOrder = -2;
    bgGlow.position.set(0, 0, -1);
    bgGlow.scale.setScalar(6);
    bgGlow.hide();

    const bus = useEventBus('pack-wax');
    const globalAssets = useGlobalAssets();
    const { rotation: matcapRotation } = useMatcap();
    const audio = useAudio();
    const canShowCard = ref(false);

    const ripWipe = useRipWipe(shaderDefault);

    const viewport = useViewportResize();

    const packRip = useManualPackRip({
        axis: 'y',
        maxDistance: computed(() => viewport.height * 0.4),
        onRipping: (val, reachedThreshold) => onManualRipping(val, reachedThreshold),
        // onFullyRipping: (val) => {
        //     if (val) refSwipeTutorial.value.startInteraction();
        //     else refSwipeTutorial.value.restartInteraction();
        // },
        onUpdate: (progress) => {
            propsTransition.progress = lerp(progress, 0.05, 0.28);

            setRipProgress();
        },
        onComplete: () => {
            lensflare.complete();

            enableRipDelay?.kill();
            showSwipeTutorial.value = false;
            open();
        },
    });
    watch(
        [() => packRip.isEnabled.value, () => packRip.isRipping.value],
        ([isEnabled, isRipping]) => {
            if (!isEnabled) {
                lensflare.stopInterval();
                return;
            }

            if (isRipping) {
                lensflare.setMode('ripping');
            } else {
                lensflare.setMode('idle');
            }
        },
        { immediate: true }
    );

    let transitionReveal, transitionRip;

    onMounted(() => {
        loader.start();
    });

    const init = () => {
        const pack = packObject.value;

        if (!pack) return;

        packMeshes.value = getAllMeshes(pack);

        pack.add(grpCustomMeshes);
        pack.add(lensflare.object);

        // vPosStart.set(0, 0, 0); // slight offset to avoid z-fighting
        // vRotStart.set(0, 0, 0);

        // vPosStart.copy(pack.position);
        // vRotStart.copy(pack.rotation);

        innerObject.add(pack);
        // innerObject.position.set(0, 0, -0.01); // slight offset to avoid z-fighting

        disposeMaterials();

        if (globalAssets.assets) setupObject();
        else globalAssets.loader.once(LoaderEvent.LOAD_COMPLETE, setupObject);

        setTransitionRip('rip');
    };

    const setupObject = () => {
        updateMaterial();

        loadEnvMap(renderer, '/textures/env-map/env-map-global.exr').then(({ map }) =>
            shaders.forEach((shader) => (shader.envMap = map.value))
        );
    };

    const disposeMaterials = () => {
        packMeshes.value.forEach((node) => disposeMaterial(node.material));
    };

    let readyCount = 0;
    let readyTriggered = false;
    const checkReady = () => {
        if (readyCount === 2 && !readyTriggered) {
            bus.emit('ready');
            bus.reset();
            readyTriggered = true;
            texturesLoaded.value = true;
        }
    };

    const setTransitionReveal = (id) => {
        if (!TRANSITIONS_REVEAL[id]) {
            packWaxLogger.warn('PackWax reveal transition not found', {
                event: 'pack.transition_missing',
                transitionType: 'reveal',
                transitionId: id,
            });
            return;
        }

        if (transitionReveal) {
            shaders.forEach((shader) => shader.clearTransitionUniforms?.({ reveal: true }));

            transitionReveal.destroy();
            transitionReveal = null;
        }
        const TransitionClass = TRANSITIONS_REVEAL[id] || FlyInWaxTransition;
        transitionReveal = new TransitionClass(packObject.value, shaderDefault, propsAnimation);
        applyTransition(transitionReveal);

        propsTransition.revealId = id;
    };

    const setTransitionRip = (id) => {
        if (!TRANSITIONS_RIP[id]) {
            packWaxLogger.warn('PackWax rip transition not found', {
                event: 'pack.transition_missing',
                transitionType: 'rip',
                transitionId: id,
            });
            return;
        }

        if (transitionRip) {
            shaders.forEach((shader) => shader.clearTransitionUniforms?.({ rip: true }));

            transitionRip.destroy();
            transitionRip = null;
        }
        const TransitionClass = TRANSITIONS_RIP[id] || RipWaxTransition;
        transitionRip = new TransitionClass(packObject.value, { propsAnimation });
        applyTransition(transitionRip);

        // audio.play('packDisolve');

        propsTransition.ripId = id;
    };

    const updateMaterial = () => {
        packMeshes.value.forEach((node) => {
            switch (node.name) {
                case 'Pack': {
                    const oldMaterial = node.material;

                    shaderDefault.alphaMap = oldMaterial.map;
                    shaderDefault.normalMap = oldMaterial.normalMap;

                    shaderDefault.roughness = oldMaterial.roughness;
                    shaderDefault.metalness = oldMaterial.metalness;
                    shaderDefault.envMap = tempTexture;

                    shaderDefault.lights = false;

                    oldMaterial.dispose();

                    node.material = shaderDefault;

                    break;
                }
            }
        });

        updateTextures();

        setImage(image.value);
        setColor(packColor.value);
    };

    const applyTransition = (transition) => {
        if (!transition) return;

        transition.createCustomMeshes?.(grpCustomMeshes);
        transition.setTransitionUniforms?.(shaders);

        shaders.forEach((shader) => (shader.transparent = transition.transparent));
    };

    const updateTextures = () => {
        const { matcapIrridescent3 } = globalAssets.assets;

        shaders.forEach((shader) => {
            shader.alphaMap = shader.alphaMap || shader.map;
            shader.uniforms.tMatcapIrri.value = matcapIrridescent3;
        });

        readyCount++;
        checkReady();
    };

    const setUniforms = (props) => {
        shaders.forEach((shader) => {
            shader.u_transition_progress = propsTransition.progress;

            shader.bumpScale = props.bumpScale;
            shader.normalScale.setScalar(props.bumpScale);

            shader.envMapRotation.setFromVector3(props.envMapRotation);
            shader.envMapIntensity = props.envMapIntensity;

            shader.uniforms.u_matcapScale.value = props.mcScale;
        });
    };

    const setImage = (url) => {
        if (!url) {
            packWaxLogger.warnOnce('PackWax texture URL missing', {
                event: 'asset.texture_missing',
                onceKey: 'asset.texture_missing:PackWax:rgb',
                textureKind: 'rgb',
                component: 'PackWax',
            });
            return;
        }
        loadBitmapTexture(url, { flipY: true }).then((texture) => {
            texture.anisotropy = renderer.capabilities.getMaxAnisotropy();
            shaders.forEach((shader) => {
                shader.map = texture;
                shader.needsUpdate = true;
            });
            readyCount++;
            checkReady();
        });
    };

    const setColor = (val) => {
        shaders.forEach((shader) => {
            shader.color.set(val);
        });
    };

    const setShowSwipeTutorial = (val) => {
        gsap.killTweensOf(vPosInteraction, 'y');
        gsap.to(vPosInteraction, {
            y: val ? -0.2 : 0,
            duration: val ? 1 : 0.8,
            ease: val ? 'power2.inOut' : 'power3.out',
        });
    };

    const setActive = (val) => {
        innerObject.visible = val;
    };

    const onManualRipping = (val, reachedThreshold) => {
        const motionAmb = val ? 0 : 1;
        const duration = val ? 0.2 : 0.4;
        const ease = val ? 'power3.out' : 'back.out(3)';

        gsap.killTweensOf(vPosInteraction);
        gsap.to(vPosInteraction, { z: val ? -0.2 : 0, duration, ease });

        if (!reachedThreshold) {
            gsap.killTweensOf(propsReactive, 'motionAmb');
            gsap.to(propsReactive, { motionAmb, duration, ease: 'power2.out' });
        }

        if (transitionRip) {
            // const spinRot = val || reachedThreshold ? Math.PI : 0;
            transitionRip.setManualMode(val || reachedThreshold);
            // transitionRip.setSpinRotation(spinRot);
        }

        if (val) {
            bgGlow.show({ intensity: 0.5, duration: 1 });
            ripWipe.stopInterval();
        } else if (!reachedThreshold) {
            bgGlow.hide({ duration: 0.3 });
            ripWipe.startInterval();
        }
    };

    const setInteractive = (val) => {
        if (val) interaction.enable();
        else interaction.disable();
    };

    const setRipProgress = () => {
        const { progress } = propsTransition;

        canShowCard.value = progress >= 0.2;

        const flarePr = map(progress, 0.05, 0.3, 0, 1, true);

        if (!lensflare.isComplete.value) lensflare.setProgress(flarePr);

        // transitionRip?.setProgress?.(progress);
        transitionRip?.setAnimationProgress?.(progress);

        shaders.forEach((shader) => (shader.u_transition_progress = progress));
    };

    const update = () => {
        const { position, rotation, positionTarget, rotationTarget } = propsAnimation;

        position.copy(positionTarget);
        rotation.copy(rotationTarget);

        const ts = performance.now() / 1000;
        const { speed, motionAmb } = propsReactive;

        // Calculate ambient position (gentle floating)
        vPosAmbient.set(0, Math.sin(ts * speed * 0.2), 0).multiplyScalar(0.04 * motionAmb);

        // Calculate ambient rotation (gentle swaying)
        vRotAmbient
            .set(
                Math.sin(ts * speed * 0.4) * 0.2,
                Math.sin(ts * speed * 0.6) * 0.4,
                Math.cos(ts * speed * 0.3) * -0.02
            )
            .multiplyScalar(0.5 * motionAmb);

        interaction.update();

        object.position
            .copy(vPosStart)
            .add(vPosInteraction)
            .add(vPosAmbient)
            .add(position)
            .add(interaction.pressMotion);
        object.rotation.setFromVector3(
            _tempRotWax.copy(vRotStart).add(vRotSpin).add(vRotAmbient).add(rotation)
        );

        innerObject.position.copy(interaction.motion.pos);
        innerObject.quaternion.copy(interaction.motion.final);

        shaders.forEach((shader) => {
            shader.u_time = performance.now() / 1000;
            shader.uniforms.u_matcapRotation.value = matcapRotation.value;
        });

        bgGlow.animate({ time: ts });

        // if (transitionRip) transitionRip.update?.(propsTransition.progress);
        if (transitionReveal) transitionReveal.update?.(propsTransition.progress);
    };

    useRafBool(active, update);

    const updateTransition = (progress) => {};

    let tlShow, tlOpen;

    const { CustomEase } = inject('gsap', { CustomEase: null });
    const customEase = 'M0,0 C0.299,0 0.284,0.794 0.501,1.004 0.66,1.157 0.749,0.971 1,1 ';

    const ease = CustomEase ? CustomEase.create('custom', customEase) : 'back.inOut(1.2)';

    const show = ({
        delay = 0,
        transitionId = propsTransition.revealId,
        spinDir = 1,
        spinDelay = 0.5,
        animate = true,
        onComplete = () => {},
    } = {}) => {
        const motionAmb = Math.max(+props.hasAmbientMotion, 0.001);

        if (transitionId) setTransitionReveal(transitionId);

        tlShow?.kill();
        tlShow = gsap
            .timeline({
                delay,
                onStart: () => {
                    transitionReveal?.start();
                    update();
                    active.value = true;
                },
                onComplete: () => {
                    onComplete();
                },
            })
            .to(propsReactive, { motionAmb, duration: 2, ease: 'sine.out' }, 0);

        if (animate) {
            tlShow.fromTo(
                vRotSpin,
                { y: 0 },
                { y: toRadians(360 * spinDir), duration: 2, ease: ease },
                spinDelay
            );
        }

        return tlShow;
    };

    const open = ({ delay = 0 } = {}) => {
        propsTransition.animating = true;
        // propsTransition.progress = 0;

        propsReactive.colorDissolve = PALETTE_DISSOLVE[randomInt(0, PALETTE_DISSOLVE.length - 1)];

        const duration = (transitionRip?.duration || 1) * (1 - propsTransition.progress);

        tlOpen?.kill();
        tlOpen = gsap
            .timeline({
                delay,
                onStart: () => {
                    active.value = true;
                    emit('openStart');
                },
                onComplete: () => {
                    active.value = false;

                    // emit('openComplete');
                },
            })
            .to(propsReactive, { motionAmb: 0, duration: 0.4, ease: 'sine.out' }, 0)
            .to(vPosInteraction, { z: 0, duration: 0.4, ease: 'sine.out' }, 0);

        tlOpen.add(() => bgGlow.reveal({ intensity: 0.8 }), 0);
        tlOpen.add(() => bgGlow.hide({ duration: 0.6 }), 0.2);
        tlOpen.add(() => emit('openComplete'), 0.2);

        if (transitionRip?.manualMode) {
            const pack = packObject.value;

            // tlOpen.add(() => transitionRip.animateDown(), 0.1);
            tlOpen.to(pack.position, { z: -0.8, duration: 1, ease: 'power2.out' }, 0);
            tlOpen.to(pack.rotation, { x: -0.5, duration: 1, ease: 'power2.out' }, 0);
            tlOpen.to(pack.position, { y: -4, duration: 0.7, ease: 'power2.in' }, 0.05);
            tlOpen.to(
                propsTransition,
                {
                    progress: 1,
                    duration: 0.7,
                    ease: 'linear.none',
                    onUpdate: setRipProgress,
                },
                0
            );
        } else {
            tlOpen.to(
                propsTransition,
                {
                    progress: 1,
                    duration,
                    ease: 'linear.none',
                    onUpdate: () => {
                        canShowCard.value = propsTransition.progress >= 0.2;
                        transitionRip?.setProgress?.(propsTransition.progress);
                    },
                },
                transitionRip?.delay || 0
            );
        }

        return tlOpen;
    };

    const hide = ({ delay = 0 } = {}) => {
        if (delay) {
            tlShow?.kill();
            tlShow = gsap.delayedCall(delay, hideComplete);
        } else {
            hideComplete();
        }
    };

    const hideComplete = () => {
        reset();
    };

    const resetTransitions = () => {
        const { position, rotation } = propsAnimation;
        position.set(0, 0, 0);
        rotation.set(0, 0, 0);

        if (transitionReveal) {
            transitionReveal.destroy();
            transitionReveal = null;
        }
        // if (transitionRip) {
        //     transitionRip.destroy();
        //     transitionRip = null;
        // }

        shaders.forEach((shader) => shader.clearTransitionUniforms?.({ reveal: true, rip: true }));
    };

    const reset = (overrides = {}) => {
        active.value = false;

        propsReactive.motionAmb = 0;

        propsTransition.progress = 0;
        propsTransition.animating = false;

        vRotAmbient.set(0, 0, 0);
        vPosAmbient.set(0, 0, 0);

        tlShow?.kill();
        tlOpen?.kill();

        resetTransitions();

        Object.assign(propsReactive, overrides);
    };

    const onLoadComplete = ({ data }) => {
        const noFlipTextures = ['mapBumpPack', 'mapEdgesPack'];

        for (const key in data) {
            const map = data[key];
            map.name = key;
            map.flipY = !noFlipTextures.includes(key);
        }

        const { gltf } = data;
        const { scene, animations } = gltf;

        packObject.value = scene;
        packObject.value.animations = animations;

        init();

        globalLoaded.value = true;
        // emit('loaded');
    };

    const globalLoaded = ref(false);
    const texturesLoaded = ref(false);
    const loaded = computed(() => globalLoaded.value && texturesLoaded.value);
    watch(loaded, (val) => {
        if (val) emit('loaded');
    }, { immediate: true });

    const loader = useLoaderComplete(
        {
            gltf: '/glb/pack-wax.glb',
            // gltf: '/glb/pack-wax-etc1s@lg.glb',
        },
        onLoadComplete
    );

    onBeforeUnmount(() => {
        disposeMaterials(packObject.value);
    });

    watch(active, setActive, { immediate: true });
    watch(() => props.interactive, setInteractive);

    watch(interaction.pinchingOrGrabbing, (val) => {
        if (!props.interactive) return;

        const motionAmb = val ? 0 : Math.max(+props.hasAmbientMotion, 0.001);
        const duration = val ? 0.5 : 4;
        const ease = val ? 'power2.out' : 'sine.in';

        gsap.killTweensOf(propsReactive, 'motionAmb');
        gsap.to(propsReactive, { motionAmb, duration, ease });
    });

    watch(image, setImage);
    watch(propsReactive, setUniforms, { immediate: true });

    watch(packColor, setColor);
    watch(testColor, setColor);
    watch(bgGlowColor, (val) => {
        bgGlow.material.uniforms.lightColor.value.set(val);
    });
    // watch(() => refSwipeTutorial.value?.active, setShowSwipeTutorial);

    let enableRipDelay = null;
    const enableRip = () => {
        const pack = packObject.value;
        if (!pack) return;

        gsap.killTweensOf(pack.rotation);
        gsap.to(pack.rotation, {
            y: Math.PI,
            duration: 0.6,
            ease: 'power3.out',
            onComplete: () => {
                packRip.enable();
                ripWipe.startInterval();

                enableRipDelay = gsap.delayedCall(1, () => (showSwipeTutorial.value = true));
            },
        });
    };

    // Expose component API
    defineExpose({
        show,
        hide,
        open,
        reset,
        object,
        setRipProgress,
        canShowCard,
        enableRip,
        shader: shaderDefault,
        exposedVectors: { vRotStart, vPosStart },
    });

    const emit = defineEmits(['loaded', 'openStart', 'openComplete']);

    const modelExport = useModelExporter();
    const pane = !modelExport
        ? usePane(
              [
                  { name: 'color', value: testColor },
                  { name: 'bgGlowColor', value: bgGlowColor },
                  { value: propsReactive },
              ],
              {
                  title: 'PackWax',
                  expanded: true,
              }
          )
        : null;

    if (pane) {
        pane.addBlade({
            view: 'list',
            label: 'Reveal',
            options: Object.keys(TRANSITIONS_REVEAL).map((key) => ({ text: key, value: key })),
            value: propsTransition.revealId,
        }).on('change', (e) => setTransitionReveal(e.value));

        pane.addBlade({
            view: 'list',
            label: 'Rip',
            options: Object.keys(TRANSITIONS_RIP).map((key) => ({ text: key, value: key })),
            value: propsTransition.ripId,
        }).on('change', (e) => setTransitionRip(e.value));

        pane.addBinding(propsTransition, 'progress', { min: 0, max: 1 }).on('change', (e) =>
            updateTransition(e.value)
        );

        // pane.addButton({ title: 'Rip Start' }).on('click', () => {
        //     transitionRip?.setProgress?.(1);
        // });
        pane.addButton({ title: 'Open' }).on('click', () => {
            open();
        });
    }
</script>

<style lang="scss" scoped>
    @use '@/styles/shared.scss' as *;

    .packWax__swipeLabel {
        position: absolute;
        top: 10%;
        left: 50%;
        transform: translateX(-50%);
    }
</style>
